import { expectedOf, gaugeBands, type GaugeBand } from "../expected";
import { PointIcon } from "../icon";
import { boundPoint, Frame, Note, useWidgetText } from "../shared";
import type { WidgetProps } from "../types";

const BAND = { ok: "stroke-green-600", warning: "stroke-amber-500", critical: "stroke-red-600" } as const;

// A half circle from 180° (min) to 0° (max), centre (50, 50), radius 40.
function at(f: number, r = 40) {
  const a = Math.PI * (1 - f);
  return [50 + r * Math.cos(a), 50 - r * Math.sin(a)] as const;
}
function arc(f0: number, f1: number) {
  const [x0, y0] = at(f0);
  const [x1, y1] = at(f1);
  return `M ${x0} ${y0} A 40 40 0 0 1 ${x1} ${y1}`;
}

/**
 * gauge: one reading as a half ring filled up to the value, in the colour of the band it is in, with the number
 * inside (DDB-35); the number shows the real value even past the ends. Without configured bands, the point's normal
 * band gives them (DDB-03).
 */
export function Gauge({ widget, input }: WidgetProps) {
  const fmt = useWidgetText();
  const bound = boundPoint(widget, input);
  const point = bound?.point;
  const cfg = widget.config ?? {};
  const pmin = point?.range?.[0] ?? 0;
  const pmax = point?.range?.[1] ?? 100;
  const min = typeof cfg.min === "number" ? cfg.min : pmin;
  const max = typeof cfg.max === "number" ? cfg.max : pmax;
  const bands = Array.isArray(cfg.bands) ? (cfg.bands as GaugeBand[]) : gaugeBands(expectedOf(point), min, max);
  const name = widget.title ?? point?.name ?? "";
  const v = point?.latest?.value;
  const has = point && typeof v === "number";
  const text = has ? fmt.value(v, point) : fmt.t("state.noData");
  const offline = bound?.device?.state === "offline";
  const frac = (x: number) => Math.min(1, Math.max(0, (x - min) / (max - min || 1)));
  // The band the value is in; past the last band, the last one.
  const level = has ? ((bands.find((b) => v <= b.to) ?? bands[bands.length - 1])?.level ?? "ok") : "ok";
  return (
    <Frame
      widget={widget}
      state={!has ? "empty" : offline ? "offline" : "normal"}
      title={name}
      icon={<PointIcon point={point} />}
    >
      <div className="relative m-auto w-full max-w-56">
        <svg viewBox="0 0 100 56" className="w-full" aria-hidden="true">
          <path d={arc(0, 1)} className="fill-none stroke-gray-100" strokeWidth={10} strokeLinecap="round" />
          {has && frac(v) > 0 && (
            <path
              d={arc(0, frac(v))}
              data-band={level}
              className={`fill-none ${BAND[level]}`}
              strokeWidth={10}
              strokeLinecap="round"
            />
          )}
        </svg>
        <p
          aria-label={`${name}: ${text}`}
          className="absolute inset-x-0 bottom-0 text-center text-2xl font-semibold tracking-tight tabular-nums text-gray-900"
        >
          {text}
        </p>
      </div>
      {offline && <Note tone="warn">{fmt.t("state.offline")}</Note>}
    </Frame>
  );
}
